import { Storage, ref, UploadTask, uploadBytesResumable, percentage, getDownloadURL, fromTask } from '@angular/fire/storage';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

export type UploadStatus = 'STARTING' | 'UPLOADING' | 'PAUSED' | 'TERMINATED' | 'CANCELED' | 'ERROR';

export class FileStorageUploadProgress {

  private task: UploadTask;
  private fileRef;

  private _downloadUrl: Subject<string> = new Subject();
  public downloadURL$: Observable<string> = this._downloadUrl.asObservable();
  public uploadPercent$: Observable<number>;

  private _progress = new BehaviorSubject({ total: 0, sent: 0, progress: 0, state: 'idle', percent: 0 });
  public progress$ = this._progress.asObservable();

  private _error: Subject<Error> = new Subject();
  public error$: Observable<Error> = this._error.asObservable();

  private _done: Subject<string> = new Subject();
  public done$: Observable<string> = this._done.asObservable().pipe(take(1));

  private _status: BehaviorSubject<UploadStatus> = new BehaviorSubject(null);
  public status$: Observable<UploadStatus> = this._status.asObservable();


  constructor(
    private service: Storage,
    file: File,
    destination: string,
    metadata?: firebase.default.storage.UploadMetadata) {

    this.fileRef = ref(this.service, destination);

    this.task = uploadBytesResumable(this.fileRef, file, metadata);

    this.uploadPercent$ = percentage(this.task as any)
      .pipe(map(res => res.progress));

      this._status.next('UPLOADING');

    fromTask(this.task as any)
      .pipe(
        finalize(async () => {
          const url = await getDownloadURL(this.fileRef);
          this._downloadUrl.next(url);
        })
      )
      .subscribe(
        (a) => {
          this._progress.next({
            total: a.totalBytes,
            sent: a.bytesTransferred,
            state: a.state,
            progress: a.bytesTransferred / a.totalBytes,
            percent: Math.percentage(a.bytesTransferred, a.totalBytes)
          });
          if (a.totalBytes > 0 && a.bytesTransferred == a.totalBytes) {
            this._done.next(a.ref.fullPath);
            this._status.next('TERMINATED');
          }
        },
        error => {
          this._error.next(error);
          this._status.next('ERROR');
        }
      );
  }

  pause() {
    if (this._status.value !== 'UPLOADING') { return false; }
    this._status.next('PAUSED');
    return this.task.pause();
  }

  continue() {
    if (this._status.value !== 'PAUSED') { return false; }
    this._status.next('PAUSED');
    return this.task.resume();
  }

  cancel() {
    const notCancelables: UploadStatus[] = [null, 'CANCELED', 'ERROR', 'STARTING', 'TERMINATED'];
    if (notCancelables.includes(this._status.value)) { return false; }
    return this.task.cancel();
  }
}
